import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { login, register, verifyOTP, resendOTP, forgotPassword, resetPassword, getProfile } from '../../utils/api';
import toast from 'react-hot-toast';

const BACKEND =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
    : process.env.NODE_ENV === 'production'
    ? 'https://api.we-intern.in'
    : 'http://localhost:5000');

/* ===================== LIGHT TRANSPARENT AUTH LAYOUT ===================== */
const AuthLayout = ({ title, subtitle, children }) => (
  <div
    style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* Soft grid */}
    <div
      style={{
        position: 'absolute',
        inset: 0,
        backgroundImage:
          'linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        pointerEvents: 'none',
      }}
    />

    {/* Transparent Card */}
    <div
      style={{
        width: '100%',
        maxWidth: '440px',
        background: 'rgba(255, 255, 255, 0.58)',
        backdropFilter: 'blur(28px)',
        WebkitBackdropFilter: 'blur(28px)',
        border: '1px solid rgba(255, 255, 255, 0.7)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.75)',
        borderRadius: '24px',
        padding: '2.5rem 2rem',
        position: 'relative',
        zIndex: 10,
      }}
    >
      <Link to="/" style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
        <img src="/welogo.png" alt="WeIntern" style={{ height: '42px', width: 'auto' }} />
      </Link>

      <h2 style={{ color: '#111', fontSize: '1.6rem', fontWeight: 700, textAlign: 'center', marginBottom: '0.4rem' }}>
        {title}
      </h2>

      {subtitle && (
        <p style={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center', fontSize: '0.9rem', marginBottom: '1.75rem' }}>
          {subtitle}
        </p>
      )}

      {children}
    </div>
  </div>
);

/* ===================== SOCIAL BUTTONS ===================== */
const SocialButtons = () => (
  <>
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1.5rem 0', color: 'rgba(0,0,0,0.4)', fontSize: '0.8rem' }}>
      <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.1)' }} />
      <span>or continue with</span>
      <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.1)' }} />
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <a
        href={`${BACKEND}/api/auth/google`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          height: '46px',
          background: 'rgba(255,255,255,0.7)',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: '12px',
          color: '#111',
          fontSize: '0.9rem',
          fontWeight: 500,
          textDecoration: 'none',
          transition: 'all 0.25s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
          e.currentTarget.style.borderColor = 'rgba(0,0,0,0.18)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
          e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18">
          <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z" />
          <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z" />
          <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z" />
          <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.31z" />
        </svg>
        Continue with Google
      </a>

      <a
        href={`${BACKEND}/api/auth/github`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          height: '46px',
          background: 'rgba(255,255,255,0.7)',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: '12px',
          color: '#111',
          fontSize: '0.9rem',
          fontWeight: 500,
          textDecoration: 'none',
          transition: 'all 0.25s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
          e.currentTarget.style.borderColor = 'rgba(0,0,0,0.18)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
          e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#111">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.44 9.8 8.2 11.38.6.1.82-.26.82-.58v-2.03c-3.34.72-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.74.08-.73.08-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.8 1.3 3.49 1 .1-.78.42-1.3.76-1.6-2.67-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.14-.3-.54-1.52.1-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 3-.4c1.02 0 2.04.13 3 .4 2.28-1.55 3.29-1.23 3.29-1.23.64 1.66.24 2.88.12 3.18.77.84 1.23 1.91 1.23 3.22 0 4.61-2.8 5.63-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.69.83.57C20.57 21.8 24 17.3 24 12c0-6.63-5.37-12-12-12z" />
        </svg>
        Continue with GitHub
      </a>
    </div>
  </>
);

/* ===================== LOGIN PAGE ===================== */
export const LoginPage = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mentorLogin = location.pathname === '/mentor/login';

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      const errorMsg = 'Please fill in all fields';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await login(form);
      loginUser(res.data.token, res.data.user);
      toast.dismiss();
      toast.success(`Welcome back, ${res.data.user.name}! 👋`, { id: 'welcome-toast' });
      navigate(
        res.data.user.role === 'admin'
          ? '/admin'
          : res.data.user.role === 'mentor'
          ? '/mentor/dashboard'
          : '/dashboard'
      );
    } catch (err) {
      const data = err.response?.data;
      if (data?.needsVerification) {
        const errorMsg = 'Please verify your email first';
        setError(errorMsg);
        toast.error(errorMsg, { duration: 5000 });
        navigate(`/verify-otp?userId=${data.userId}`);
      } else {
        const errorMsg = data?.message || 'Invalid email or password';
        setError(errorMsg);
        toast.error(errorMsg, { duration: 5000 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={mentorLogin ? 'Mentor Login 👨‍🏫' : 'Welcome Back 👋'}
      subtitle={mentorLogin ? 'Sign in to your WeIntern mentor account' : 'Sign in to your WeIntern account'}
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <div
            style={{
              background: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.25)',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              color: '#dc2626',
              fontSize: '0.88rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Email */}
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>
            Email Address
          </label>
          <input
            type="email"
            name="email"
            placeholder="your@email.com"
            value={form.email}
            onChange={handleChange}
            required
            autoFocus
            style={{
              width: '100%',
              height: '46px',
              padding: '0 1rem',
              background: 'rgba(255,255,255,0.65)',
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: '12px',
              color: '#111',
              fontSize: '0.92rem',
              outline: 'none',
              transition: 'all 0.25s ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(0,0,0,0.3)';
              e.target.style.background = 'rgba(255,255,255,0.85)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(0,0,0,0.12)';
              e.target.style.background = 'rgba(255,255,255,0.65)';
            }}
          />
        </div>

        {/* Password */}
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPass ? 'text' : 'password'}
              name="password"
              placeholder="Your password"
              value={form.password}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                height: '46px',
                padding: '0 1rem',
                background: 'rgba(255,255,255,0.65)',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '12px',
                color: '#111',
                fontSize: '0.92rem',
                outline: 'none',
                transition: 'all 0.25s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(0,0,0,0.3)';
                e.target.style.background = 'rgba(255,255,255,0.85)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0,0,0,0.12)';
                e.target.style.background = 'rgba(255,255,255,0.65)';
              }}
            />
            <button
              type="button"
              onClick={() => setShowPass((s) => !s)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.1rem',
                color: 'rgba(0,0,0,0.45)',
              }}
            >
              {showPass ? '🙈' : '👁️'}
            </button>
          </div>
        </div>

        {/* Forgot password - RED + underline on hover */}
        <div style={{ textAlign: 'right', marginBottom: '1.25rem' }}>
          <Link
            to="/forgot-password"
            style={{
              color: '#dc2626',
              fontSize: '0.85rem',
              fontWeight: 500,
              textDecoration: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
              e.currentTarget.style.color = '#b91c1c';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
              e.currentTarget.style.color = '#dc2626';
            }}
          >
            Forgot password?
          </Link>
        </div>

        {/* Sign In Button */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            height: '48px',
            background: '#111',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.65 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.25s ease',
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.background = '#000';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#111';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {loading ? 'Signing in...' : 'Sign In →'}
        </button>
      </form>

      <SocialButtons />

      {/* Sign up free - BLUE + underline */}
      <p style={{ textAlign: 'center', marginTop: '1.5rem', color: 'rgba(0,0,0,0.55)', fontSize: '0.88rem' }}>
        Don't have an account?{' '}
        <Link
          to="/register"
          style={{
            color: '#2563eb',
            fontWeight: 600,
            textDecoration: 'underline',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#1d4ed8';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#2563eb';
          }}
        >
          Sign up free
        </Link>
      </p>
    </AuthLayout>
  );
};

/* ===================== REGISTER PAGE ===================== */
export const RegisterPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setForm((f) => ({ ...f, [name]: value.replace(/\D/g, '') }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const getStrength = (p) => {
    if (!p) return 0;
    let s = 0;
    if (p.length >= 6) s++;
    if (p.length >= 10) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };
  const strength = getStrength(form.password);
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Please enter your full name');
    if (!form.email.trim()) return toast.error('Please enter your email address');
    if (!form.phone.trim()) return toast.error('Please enter your mobile number');
    if (!/^\d{10}$/.test(form.phone.trim())) return toast.error('Mobile number must be exactly 10 digits');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.password !== form.confirm) return toast.error('Passwords do not match');

    setLoading(true);
    try {
      const res = await register({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        password: form.password,
      });
      if (res.data.success) {
        toast.success('Account created! Check your email for OTP 📧');
        navigate(`/verify-otp?userId=${res.data.userId}`);
      } else {
        toast.error(res.data.message || 'Registration failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Create Account 🌱" subtitle="Join thousands of students building their future">
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.15rem' }}>
          <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>Full Name</label>
          <input
            type="text"
            name="name"
            placeholder="Your full name"
            value={form.name}
            onChange={handleChange}
            required
            autoFocus
            disabled={loading}
            style={{
              width: '100%',
              height: '46px',
              padding: '0 1rem',
              background: 'rgba(255,255,255,0.65)',
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: '12px',
              color: '#111',
              fontSize: '0.92rem',
              outline: 'none',
            }}
          />
        </div>

        <div style={{ marginBottom: '1.15rem' }}>
          <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>Email Address</label>
          <input
            type="email"
            name="email"
            placeholder="your@email.com"
            value={form.email}
            onChange={handleChange}
            required
            disabled={loading}
            style={{
              width: '100%',
              height: '46px',
              padding: '0 1rem',
              background: 'rgba(255,255,255,0.65)',
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: '12px',
              color: '#111',
              fontSize: '0.92rem',
              outline: 'none',
            }}
          />
        </div>

        <div style={{ marginBottom: '1.15rem' }}>
          <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>Mobile Number</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(0,0,0,0.5)', fontSize: '0.9rem', fontWeight: 600 }}>+91</span>
            <input
              type="tel"
              name="phone"
              placeholder="10-digit mobile number"
              value={form.phone}
              onChange={handleChange}
              required
              maxLength={10}
              disabled={loading}
              style={{
                width: '100%',
                height: '46px',
                padding: '0 1rem 0 2.8rem',
                background: 'rgba(255,255,255,0.65)',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '12px',
                color: '#111',
                fontSize: '0.92rem',
                outline: 'none',
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '1.15rem' }}>
          <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPass ? 'text' : 'password'}
              name="password"
              placeholder="Min. 6 characters"
              value={form.password}
              onChange={handleChange}
              required
              minLength={6}
              disabled={loading}
              style={{
                width: '100%',
                height: '46px',
                padding: '0 1rem',
                background: 'rgba(255,255,255,0.65)',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '12px',
                color: '#111',
                fontSize: '0.92rem',
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={() => setShowPass((s) => !s)}
              disabled={loading}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.1rem',
                color: 'rgba(0,0,0,0.45)',
              }}
            >
              {showPass ? '🙈' : '👁️'}
            </button>
          </div>
          {form.password && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.4rem' }}>
              <div style={{ flex: 1, height: '4px', background: 'rgba(0,0,0,0.08)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(strength / 5) * 100}%`, background: '#111', borderRadius: '4px' }} />
              </div>
              <span style={{ color: '#111', fontSize: '0.72rem', fontWeight: 600 }}>{strengthLabel}</span>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '1.15rem' }}>
          <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>Confirm Password</label>
          <input
            type="password"
            name="confirm"
            placeholder="Repeat password"
            value={form.confirm}
            onChange={handleChange}
            required
            disabled={loading}
            style={{
              width: '100%',
              height: '46px',
              padding: '0 1rem',
              background: 'rgba(255,255,255,0.65)',
              border: '1px solid rgba(0,0,0,0.12)',
              borderRadius: '12px',
              color: '#111',
              fontSize: '0.92rem',
              outline: 'none',
            }}
          />
          {form.confirm && form.password !== form.confirm && (
            <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.78rem', color: '#111', fontWeight: 500 }}>
              Passwords don't match
            </span>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || form.password !== form.confirm}
          style={{
            width: '100%',
            height: '48px',
            background: '#111',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading || form.password !== form.confirm ? 0.65 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'all 0.25s ease',
          }}
          onMouseEnter={(e) => {
            if (!loading && form.password === form.confirm) {
              e.currentTarget.style.background = '#000';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#111';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {loading ? 'Creating account...' : 'Create Account →'}
        </button>
      </form>

      <SocialButtons />

      <p style={{ textAlign: 'center', marginTop: '1.5rem', color: 'rgba(0,0,0,0.55)', fontSize: '0.88rem' }}>
        Already have an account?{' '}
        <Link
          to="/login"
          style={{
            color: '#2563eb',
            fontWeight: 600,
            textDecoration: 'underline',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#1d4ed8';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#2563eb';
          }}
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
};

/* ===================== OTP PAGE ===================== */
export const OTPPage = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const userId = params.get('userId');

  useEffect(() => {
    document.getElementById('otp-0')?.focus();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleOtpChange = (val, idx) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[idx] = val.slice(-1);
    setOtp(next);
    if (val && idx < 5) document.getElementById(`otp-${idx + 1}`)?.focus();
    if (val && idx === 5 && [...next.slice(0, 5), val.slice(-1)].every((d) => d !== ''))
      setTimeout(() => document.getElementById('otp-submit')?.click(), 100);
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) document.getElementById(`otp-${idx - 1}`)?.focus();
    if (e.key === 'ArrowLeft' && idx > 0) document.getElementById(`otp-${idx - 1}`)?.focus();
    if (e.key === 'ArrowRight' && idx < 5) document.getElementById(`otp-${idx + 1}`)?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    const next = [...otp];
    text.split('').forEach((d, i) => {
      if (i < 6) next[i] = d;
    });
    setOtp(next);
    document.getElementById(`otp-${Math.min(text.length, 5)}`)?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const otpStr = otp.join('');
    if (otpStr.length !== 6) {
      toast.error('Enter all 6 digits');
      return;
    }
    setLoading(true);
    try {
      const res = await verifyOTP({ userId, otp: otpStr });
      loginUser(res.data.token, res.data.user);
      toast.success('Email verified! Welcome to WeIntern 🎉');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setResending(true);
    try {
      const response = await resendOTP({ userId });
      if (response.data.success) {
        toast.success('New OTP sent! Check your email.');
        setCountdown(60);
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0')?.focus();
      } else {
        toast.error(response.data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout title="Verify Email 📧" subtitle="Enter the 6-digit OTP sent to your email address">
      <div style={{ textAlign: 'center', color: 'rgba(0,0,0,0.6)', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
        <span>🔒</span> OTP expires in 10 minutes
      </div>

      <form onSubmit={handleVerify}>
        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', marginBottom: '1.5rem' }} onPaste={handlePaste}>
          {otp.map((d, i) => (
            <input
              key={i}
              id={`otp-${i}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleOtpChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              style={{
                width: '48px',
                height: '52px',
                textAlign: 'center',
                fontSize: '1.25rem',
                fontWeight: 600,
                background: 'rgba(255,255,255,0.7)',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '12px',
                color: '#111',
                outline: 'none',
              }}
            />
          ))}
        </div>

        <button
          id="otp-submit"
          type="submit"
          disabled={loading || otp.join('').length !== 6}
          style={{
            width: '100%',
            height: '48px',
            background: '#111',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: loading || otp.join('').length !== 6 ? 'not-allowed' : 'pointer',
            opacity: loading || otp.join('').length !== 6 ? 0.65 : 1,
            transition: 'all 0.25s ease',
          }}
          onMouseEnter={(e) => {
            if (!loading && otp.join('').length === 6) {
              e.currentTarget.style.background = '#000';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#111';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {loading ? 'Verifying...' : 'Verify OTP →'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: '1.25rem', color: 'rgba(0,0,0,0.55)', fontSize: '0.88rem' }}>
        Didn't receive it?{' '}
        {countdown > 0 ? (
          <span>Resend in {countdown}s</span>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending}
            style={{ background: 'none', border: 'none', color: '#2563eb', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
          >
            {resending ? 'Sending...' : 'Resend OTP'}
          </button>
        )}
      </p>

      <p style={{ textAlign: 'center', marginTop: '0.75rem' }}>
        <Link to="/login" style={{ color: '#111', textDecoration: 'none' }}>
          ← Back to Sign In
        </Link>
      </p>
    </AuthLayout>
  );
};

/* ===================== FORGOT PASSWORD ===================== */
export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devMode, setDevMode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    setLoading(true);
    try {
      const response = await forgotPassword({ email });
      if (response.data.success) {
        setSent(true);
        if (response.data.message.includes('console')) {
          setDevMode(true);
          toast.success('Reset link generated! Check server console 🖥️', { duration: 6000 });
        } else {
          toast.success('Reset link sent! Check your email inbox');
        }
      } else {
        toast.error(response.data.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Forgot Password 🔑"
      subtitle={sent ? 'Check your inbox for the reset link.' : "Enter your email and we'll send a reset link"}
    >
      {!sent ? (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.15rem' }}>
            <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>
              Email Address
            </label>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              disabled={loading}
              style={{
                width: '100%',
                height: '46px',
                padding: '0 1rem',
                background: 'rgba(255,255,255,0.65)',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '12px',
                color: '#111',
                fontSize: '0.92rem',
                outline: 'none',
              }}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !email}
            style={{
              width: '100%',
              height: '48px',
              background: '#111',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading || !email ? 0.65 : 1,
              transition: 'all 0.25s ease',
            }}
            onMouseEnter={(e) => {
              if (!loading && email) {
                e.currentTarget.style.background = '#000';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#111';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {loading ? 'Sending...' : 'Send Reset Link →'}
          </button>
        </form>
      ) : (
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</div>
          <p style={{ color: '#111' }}>
            {devMode ? (
              <>
                <strong>Development Mode:</strong> Check your server console for the reset link.
              </>
            ) : (
              <>
                Reset link sent to <strong>{email}</strong>. Expires in 1 hour.
              </>
            )}
          </p>
          <button
            onClick={() => {
              setSent(false);
              setEmail('');
              setDevMode(false);
            }}
            style={{
              marginTop: '1rem',
              padding: '0.6rem 1.2rem',
              border: '1px solid rgba(0,0,0,0.15)',
              borderRadius: '10px',
              background: 'transparent',
              color: '#111',
              cursor: 'pointer',
            }}
          >
            Send to Different Email
          </button>
        </div>
      )}
      <p style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <Link to="/login" style={{ color: '#111', textDecoration: 'none' }}>
          ← Back to Sign In
        </Link>
      </p>
    </AuthLayout>
  );
};

/* ===================== RESET PASSWORD ===================== */
export const ResetPasswordPage = () => {
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const email = params.get('email');

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ email, token, password: form.password });
      setDone(true);
      toast.success('Password reset!');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed. Link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Reset Password 🔐" subtitle="Set your new password below">
      {!done ? (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.15rem' }}>
            <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>
              New Password
            </label>
            <input
              type="password"
              name="password"
              placeholder="Min. 6 characters"
              value={form.password}
              onChange={handleChange}
              required
              minLength={6}
              autoFocus
              style={{
                width: '100%',
                height: '46px',
                padding: '0 1rem',
                background: 'rgba(255,255,255,0.65)',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '12px',
                color: '#111',
                fontSize: '0.92rem',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ marginBottom: '1.15rem' }}>
            <label style={{ display: 'block', color: 'rgba(0,0,0,0.7)', fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.4rem' }}>
              Confirm New Password
            </label>
            <input
              type="password"
              name="confirm"
              placeholder="Repeat new password"
              value={form.confirm}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                height: '46px',
                padding: '0 1rem',
                background: 'rgba(255,255,255,0.65)',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '12px',
                color: '#111',
                fontSize: '0.92rem',
                outline: 'none',
              }}
            />
            {form.confirm && form.password !== form.confirm && (
              <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.78rem', color: '#111', fontWeight: 500 }}>
                Passwords don't match
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              height: '48px',
              background: '#111',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.65 : 1,
              transition: 'all 0.25s ease',
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.background = '#000';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#111';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {loading ? 'Resetting...' : 'Reset Password →'}
          </button>
        </form>
      ) : (
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</div>
          <p style={{ color: '#111' }}>Password reset! Redirecting to login...</p>
        </div>
      )}
    </AuthLayout>
  );
};

/* ===================== AUTH CALLBACK ===================== */
export const AuthCallback = () => {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      setError(true);
      return;
    }
    localStorage.setItem('wi_token', token);
    getProfile()
      .then((res) => {
        loginUser(token, res.data.data);
        toast.dismiss();
        toast.success(`Welcome, ${res.data.data.name}! 🎉`, { id: 'welcome-toast' });
        navigate(res.data.data.role === 'admin' ? '/admin' : '/dashboard');
      })
      .catch(() => setError(true));
  }, []);

  if (error)
    return (
      <AuthLayout title="Authentication Failed">
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>❌</div>
          <p style={{ color: '#111' }}>
            Something went wrong. <Link to="/login">Try again</Link>
          </p>
        </div>
      </AuthLayout>
    );

  return (
    <AuthLayout title="Signing you in...">
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <div
          style={{
            width: '36px',
            height: '36px',
            border: '3px solid rgba(0,0,0,0.1)',
            borderTopColor: '#111',
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <p style={{ color: 'rgba(0,0,0,0.55)', marginTop: '1rem' }}>Please wait ✨</p>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </AuthLayout>
  );
};